import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MotifIcon } from "@ey-xd/motif-react";
import { mailToIcon } from "../../common/MotifIcons";
import "./outage.scss";
import outageImage from "../../common/images/Outage.svg";
import { getPlannedOutageActiveRequest } from "../partnerFileUpload/redux/partnerFileUploadSlice";
import usePartnerFileUpload from "../partnerFileUpload/hooks/usePartnerFileUpload";
import { ApiStatus } from "../../common/services/api-status.enum";
import { getUserTimezoneAbbr } from "../../common/utility/utility";

const REDIRECT_TO_LOGIN_PATH = "/";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

// Formats an ISO/UTC date string as "20 June 2026, 00:00 IST" in the user's local timezone.
// The API returns UTC timestamps, sometimes without a trailing "Z" (e.g. "2026-07-30T10:00:00").
// We normalise the value to UTC and then use local Date getters so the outage page reflects
// the user's system timezone while keeping the display format unchanged.
const formatOutageDateTime = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(raw) || /GMT|UTC/i.test(raw);
    const iso = hasTimezone || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)
        ? raw
        : `${raw}Z`;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const day = date.getDate();
    const month = MONTH_NAMES[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const tz = getUserTimezoneAbbr();
    return `${day} ${month} ${year}, ${hours}:${minutes}${tz ? ` ${tz}` : ""}`;
};

// Parses a UTC timestamp string (with or without trailing "Z") into an epoch ms value,
// treating timezone-less strings as UTC (not local time).
const parseUtcToEpochMs = (value) => {
    if (!value) return NaN;
    const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
    const isoValue = hasTimezone ? value : `${value}Z`;
    return new Date(isoValue).getTime();
};

const OutagePage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { plannedOutageActiveData } = usePartnerFileUpload();
    const localTimeoutRef = useRef(null);

    const outageData = plannedOutageActiveData?.data;
    const endFormatted = formatOutageDateTime(outageData?.outageEndsUtc);

    // The active outage API is triggered from Layout (either via the axios
    // interceptor/outage monitor when the start time is reached, or via the
    // /maintenance route guard on manual navigation/refresh). This page just
    // reads the redux state and schedules the end-time re-check below.
    useEffect(() => {
        return () => {
            if (localTimeoutRef.current) clearTimeout(localTimeoutRef.current);
        };
    }, []);

    // Once the API confirms the outage is over, redirect to login (user must sign in
    // again). If the user is flagged to bypass the outage, send them straight to the
    // data upload page instead of keeping them stuck on the maintenance screen.
    useEffect(() => {
        if (plannedOutageActiveData?.status !== ApiStatus.SUCCESS) return;

        if (outageData?.isBypassUser) {
            navigate(REDIRECT_TO_LOGIN_PATH, { replace: true });
            return;
        }

        const isStillInOutage = !!outageData?.isDuringOutage;

        if (!isStillInOutage) {
            navigate(REDIRECT_TO_LOGIN_PATH, { replace: true });
            return;
        }

        // Fast-path: schedule a re-check right at the announced end time instead of
        // waiting for the next poll interval.
        if (outageData?.outageEndsUtc) {
            const msRemaining = parseUtcToEpochMs(outageData.outageEndsUtc) - Date.now();
            if (localTimeoutRef.current) clearTimeout(localTimeoutRef.current);
            if (msRemaining > 0) {
                localTimeoutRef.current = setTimeout(() => {
                    dispatch(getPlannedOutageActiveRequest());
                }, msRemaining + 1000);
            }
        }
    }, [plannedOutageActiveData, outageData, navigate, dispatch]);

    const baseMessage = outageData?.message || "Scheduled Maintenance in Progress";
    const title = baseMessage;
    const baseSupportingMessage = outageData?.supportingMessage
        || "The EY-Parthenon Data Bureau Submission Portal is currently unavailable due to planned maintenance.";
    const resumeByText = "Service is expected to resume by";

    return (
        <div className="outage-page">
            <div className="outage-page__content">
                <img src={outageImage} alt="Scheduled maintenance" className="outage-page__image" />
                <div className="outage-page__details">
                    <h2 className="outage-page__title">{title}</h2>
                    <p className="outage-page__message">
                        {baseSupportingMessage}
                        {endFormatted && (
                            <>
                                <br />
                                {resumeByText} <strong>{endFormatted}.</strong>
                            </>
                        )}
                    </p>
                    <br />
                    <p className="outage-page__support-text">
                        If you have any question, please contact technical support at
                    </p>
                    <a href="mailto:DBS@parthenon.ey.com" className="outage-page__support-link">
                        <MotifIcon src={mailToIcon} className="outage-page__mail-icon" />
                        DBS@parthenon.ey.com
                    </a>
                </div>
            </div>
        </div>
    );
};

export default OutagePage;
